import { QaiCallAudioIndicatorSize, QaiCallAudioIndicatorStatus, QaiIconKind } from '@qai/ui';
import { ScreenSharePipControls, ScreenSharePipDevice } from './screen-share-pip-controls';

export const PIP_VIDEO_CLASS = 'pip-video';

const STOP_SHARE_SVG =
  '<svg xmlns="http://www.w3.org/2000/svg" class="pip-icon--stroke" viewBox="0 0 24 24" aria-hidden="true"><path d="M4 10v8a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2v-8"/><path d="M12 3v11M8 10l4 4 4-4"/></svg>';

const PIP_STYLES = `
  html,
  body {
    height: 100%;
  }

  body {
    background-color: var(--bg-primary);
    color: var(--text-primary);
    display: flex;
    flex-direction: column;
    font: 400 var(--type-text-xs);
    margin: 0;
    overflow: hidden;
  }

  .pip-stage {
    background-image: linear-gradient(
      160deg,
      var(--bg-primary) 0%,
      color-mix(in srgb, var(--bg-brand-solid) 55%, var(--bg-primary)) 55%,
      color-mix(in srgb, var(--bg-success-solid) 55%, var(--bg-brand-solid)) 100%
    );
    display: flex;
    flex: 1;
    flex-direction: column;
    gap: var(--spacing-lg);
    min-height: 0;
    padding: var(--spacing-lg);
  }

  .pip-tile {
    background-color: var(--bg-secondary);
    border-radius: var(--radius-md);
    flex: 1;
    min-height: 0;
    overflow: hidden;
    position: relative;
  }

  body .pip-tile > video.${PIP_VIDEO_CLASS} {
    display: block;
    height: 100%;
    inset: auto;
    object-fit: cover;
    position: static;
    width: 100%;
  }

  /* Mirrored like the self-view on the call screen (.userVideo video in call-practice). */
  body .pip-tile--user > video.${PIP_VIDEO_CLASS} {
    transform: scaleX(-1);
  }

  .pip-tile__initials {
    align-items: center;
    background-color: var(--bg-quaternary);
    display: none;
    font: 600 var(--type-text-xl);
    inset: 0;
    justify-content: center;
    position: absolute;
  }

  .pip-tile__initials--visible {
    display: flex;
  }

  /* Matches the speaking ring of qai-call-video on the call screen. */
  .pip-tile--speaking {
    box-shadow: 0 0 0 6px rgb(from var(--border-success-solid) r g b / 25%);
  }

  .pip-indicator {
    position: absolute;
    z-index: 100;
  }

  .pip-indicator[hidden] {
    display: none;
  }

  .pip-indicator--audio {
    inset-block-start: var(--spacing-md);
    inset-inline-end: var(--spacing-md);
  }

  .pip-indicator--loading {
    inset-block-start: 50%;
    inset-inline-start: 50%;
    transform: translate(-50%, -50%);
  }

  .pip-toolbar {
    align-items: center;
    display: flex;
    gap: var(--spacing-lg);
    justify-content: center;
    padding-block: var(--spacing-lg);
  }

  .pip-button {
    align-items: center;
    background-color: var(--button-tertiary-bg-default);
    border: 1px solid var(--button-tertiary-border);
    border-radius: var(--radius-full);
    color: var(--button-tertiary-text);
    cursor: pointer;
    display: inline-flex;
    height: 32px;
    justify-content: center;
    padding: 0;
    transition-duration: 150ms;
    transition-property: background-color;
    width: 32px;
  }

  .pip-button-wrap {
    display: inline-flex;
    position: relative;
  }

  .pip-badge {
    align-items: center;
    background-color: #fff;
    border-radius: var(--radius-full);
    box-sizing: border-box;
    color: var(--fg-error-primary);
    display: flex;
    height: 20px;
    justify-content: center;
    left: 50%;
    padding: 2px;
    pointer-events: none;
    position: absolute;
    top: -6px;
    transform: translateX(calc(16px - 50%));
    width: 20px;
  }

  .pip-badge[hidden] {
    display: none;
  }

  .pip-badge svg {
    fill: currentColor;
    height: 16px;
    width: 16px;
  }

  /* Matches the call screen's tooltip override in call.component.scss. */
  .pip-tooltip {
    animation: pip-tooltip-show 150ms cubic-bezier(0, 0, 0.2, 1) forwards;
    background-color: var(--bg-secondary);
    border-radius: var(--radius-sm);
    box-shadow:
      0 12px 16px -4px rgb(16 24 40 / 8%),
      0 4px 6px -2px rgb(16 24 40 / 3%);
    box-sizing: border-box;
    color: var(--text-primary);
    font: 600 var(--type-text-xs);
    max-width: 200px;
    overflow-wrap: anywhere;
    padding-block: var(--spacing-md);
    padding-inline: var(--spacing-lg);
    pointer-events: none;
    position: fixed;
    text-align: center;
    z-index: 1000;
  }

  .pip-tooltip[hidden] {
    display: none;
  }

  @keyframes pip-tooltip-show {
    0% {
      opacity: 0;
      transform: scale(0.8);
    }

    100% {
      opacity: 1;
      transform: scale(1);
    }
  }

  .pip-button--active {
    background-color: var(--button-tertiary-bg-hover);
    border-color: var(--fg-brand-primary);
  }

  .pip-button--destructive {
    background-color: var(--button-destructive-bg-default);
    border-color: var(--button-destructive-border);
    color: var(--button-destructive-text);
  }

  .pip-button:disabled,
  .pip-settings__select:disabled {
    cursor: not-allowed;
    opacity: 0.6;
  }

  @media (hover: hover) {
    .pip-button:enabled:hover {
      background-color: var(--button-tertiary-bg-hover);
    }

    .pip-button--destructive:enabled:hover,
    .pip-confirm__end:hover {
      background-color: var(--button-destructive-bg-hover);
    }

    .pip-confirm__resume:hover {
      background-color: var(--button-primary-bg-hover);
    }
  }

  .pip-button:focus-visible,
  .pip-confirm__action:focus-visible,
  .pip-settings__select:focus-visible {
    outline: 2px solid var(--fg-brand-primary);
    outline-offset: var(--spacing-xxs);
  }

  .pip-button svg {
    fill: currentColor;
    height: 16px;
    width: 16px;
  }

  svg.pip-icon--stroke {
    fill: none;
    stroke: currentColor;
    stroke-linecap: round;
    stroke-linejoin: round;
    stroke-width: 2;
  }

  .pip-settings {
    border-block-start: 1px solid var(--border-secondary);
    display: flex;
    flex-direction: column;
    gap: var(--spacing-md);
    padding-block-start: var(--spacing-lg);
    padding-inline: var(--spacing-lg);
  }

  .pip-settings__field {
    display: flex;
    flex-direction: column;
    gap: var(--spacing-xs);
  }

  .pip-settings__label {
    color: var(--text-secondary);
    font: 500 var(--type-text-xs);
  }

  .pip-settings__select {
    background-color: var(--bg-secondary);
    border: 1px solid var(--border-primary);
    border-radius: var(--radius-sm);
    color: var(--text-primary);
    font: 400 var(--type-text-xs);
    min-width: 0;
    padding-block: var(--spacing-sm);
    padding-inline: var(--spacing-md);
  }

  .pip-settings__error {
    align-items: center;
    background-color: var(--bg-secondary);
    border: 1px solid var(--border-primary);
    border-radius: var(--radius-sm);
    color: var(--fg-error-primary);
    display: flex;
    font: 400 var(--type-text-xs);
    gap: var(--spacing-sm);
    padding-block: var(--spacing-sm);
    padding-inline: var(--spacing-md);
  }

  .pip-settings__error svg {
    fill: currentColor;
    flex-shrink: 0;
    height: 16px;
    width: 16px;
  }

  .pip-confirm {
    align-items: center;
    background-color: rgb(from var(--bg-primary) r g b / 92%);
    display: flex;
    flex-direction: column;
    gap: var(--spacing-lg);
    inset: 0;
    justify-content: center;
    padding: var(--spacing-3xl);
    position: fixed;
    text-align: center;
    z-index: 1000;
  }

  .pip-confirm__title {
    font: 600 var(--type-text-sm);
  }

  .pip-confirm__message {
    color: var(--text-secondary);
  }

  .pip-confirm__actions {
    display: flex;
    gap: var(--spacing-md);
    margin-block-start: var(--spacing-md);
  }

  .pip-confirm__action {
    border: none;
    border-radius: var(--radius-sm);
    color: var(--button-primary-text);
    cursor: pointer;
    font: 600 var(--type-text-xs);
    padding-block: var(--spacing-md);
    padding-inline: var(--spacing-xl);
    transition-duration: 150ms;
    transition-property: background-color;
  }

  .pip-confirm__end {
    background-color: var(--button-destructive-bg-default);
  }

  .pip-confirm__resume {
    background-color: var(--button-primary-bg-default);
  }
`;

export class ScreenSharePipView {
  readonly avatarSlot: HTMLElement;
  readonly userSlot: HTMLElement;

  private readonly doc: Document;
  private readonly initials: HTMLElement;
  private readonly avatarAudio: HTMLElement;
  private readonly avatarLoading: HTMLElement;
  private readonly userAudio: HTMLElement;
  private readonly toolbar: HTMLElement;
  private readonly settingsButton?: HTMLButtonElement;
  private settingsPanel: HTMLElement | null = null;
  private settingsPanelHeight = 0;
  private readonly endCallButton: HTMLButtonElement;
  private readonly micButton?: HTMLButtonElement;
  private readonly cameraButton?: HTMLButtonElement;
  private readonly cameraBadge?: HTMLElement;
  private readonly cameraTooltip?: HTMLElement;
  private confirm: HTMLElement | null = null;
  private readonly currentIcons = new WeakMap<HTMLElement, string>();

  constructor(
    private readonly pipWindow: Window,
    private readonly controls: ScreenSharePipControls,
    private readonly icon: (kind: QaiIconKind) => string,
    private readonly indicator: (
      size: QaiCallAudioIndicatorSize,
      status: QaiCallAudioIndicatorStatus,
    ) => HTMLElement,
    reduced: boolean,
  ) {
    this.doc = pipWindow.document;
    this.doc.documentElement.classList.add('theme-dark');

    const style = this.doc.createElement('style');
    style.textContent = PIP_STYLES;
    this.doc.head.appendChild(style);

    const t = (key: string) => controls.translate(key);

    const stage = this.element('main', 'pip-stage', this.doc.body);
    this.avatarSlot = this.element('div', 'pip-tile', stage);
    this.userSlot = this.element('div', 'pip-tile pip-tile--user', stage);
    this.initials = this.element('div', 'pip-tile__initials', this.userSlot);
    this.avatarAudio = this.placeIndicator(this.avatarSlot, 'medium', 'audio');
    this.avatarLoading = this.placeIndicator(this.avatarSlot, 'medium', 'loading');
    this.userAudio = this.placeIndicator(this.userSlot, 'small', 'audio');

    const toolbar = this.element('footer', 'pip-toolbar', this.doc.body);
    this.toolbar = toolbar;
    if (!reduced) {
      this.settingsButton = this.button(
        'pip-button',
        t('SETTINGS'),
        this.icon('settings'),
        toolbar,
        () => this.toggleSettings(),
      );
      this.settingsButton.setAttribute('aria-expanded', 'false');
    }
    this.button('pip-button', t('SCREENSHARE_STOP_LABEL'), STOP_SHARE_SVG, toolbar, () =>
      controls.stopShare(),
    );
    if (!reduced) {
      this.micButton = this.button('pip-button', t('AUDIO'), '', toolbar, () => controls.toggleMic());
      const cameraWrap = this.element('span', 'pip-button-wrap', toolbar);
      this.cameraButton = this.button('pip-button', t('VIDEO_LABEL'), '', cameraWrap, () =>
        controls.isCameraUnavailable() ? this.toggleSettings() : controls.toggleCamera(),
      );
      this.cameraBadge = this.element('span', 'pip-badge', cameraWrap);
      this.cameraBadge.setAttribute('aria-hidden', 'true');
      this.cameraBadge.hidden = true;
      this.setIcon(this.cameraBadge, this.icon('alert-circle'));
      this.cameraTooltip = this.cameraAccessTooltip(this.cameraButton);
    }
    this.endCallButton = this.button(
      'pip-button pip-button--destructive',
      t('END_CALL'),
      this.icon('phone-call'),
      toolbar,
      () => controls.endCall(),
    );

    this.refresh();
  }

  refresh(): void {
    const title = this.controls.title();
    if (this.doc.title !== title) this.doc.title = title;

    if (this.micButton) {
      const micOn = this.controls.isMicOn();
      this.setIcon(this.micButton, this.icon(micOn ? 'microphone' : 'microphone-off'));
      this.micButton.classList.toggle('pip-button--destructive', !micOn);
      this.micButton.disabled = this.controls.isMicDisabled();
    }

    if (this.cameraButton && this.cameraBadge && this.cameraTooltip) {
      const cameraOn = this.controls.isCameraOn();
      const cameraUnavailable = this.controls.isCameraUnavailable();
      this.setIcon(this.cameraButton, this.icon(cameraOn ? 'video-recorder' : 'video-recorder-off'));
      this.cameraButton.classList.toggle('pip-button--destructive', !cameraOn);
      this.cameraBadge.hidden = !cameraUnavailable;
      if (cameraUnavailable) {
        this.cameraButton.removeAttribute('title');
        this.cameraButton.setAttribute('aria-describedby', this.cameraTooltip.id);
      } else {
        this.cameraButton.title = this.controls.translate('VIDEO_LABEL');
        this.cameraButton.removeAttribute('aria-describedby');
        this.cameraTooltip.hidden = true;
      }
    }

    this.initials.textContent = this.controls.userInitials();
    this.initials.classList.toggle('pip-tile__initials--visible', this.controls.showUserInitials());

    const avatarStatus = this.controls.avatarStatus();
    const avatarSpeaking = avatarStatus === 'speaking';
    this.avatarSlot.classList.toggle('pip-tile--speaking', avatarSpeaking);
    this.userSlot.classList.toggle('pip-tile--speaking', !avatarSpeaking);
    this.avatarAudio.hidden = !avatarSpeaking;
    this.avatarLoading.hidden = avatarStatus !== 'thinking';
    this.userAudio.hidden = !this.controls.isUserSpeaking();

    this.endCallButton.disabled = this.controls.isEndCallDisabled();

    const confirmOpen = this.controls.isEndCallConfirmOpen();
    if (confirmOpen && !this.confirm) {
      this.confirm = this.buildConfirm();
    } else if (!confirmOpen && this.confirm) {
      this.confirm.remove();
      this.confirm = null;
    }
  }

  private buildConfirm(): HTMLElement {
    const t = (key: string) => this.controls.translate(key);
    const overlay = this.element('div', 'pip-confirm', this.doc.body);
    overlay.setAttribute('role', 'alertdialog');
    overlay.setAttribute('aria-modal', 'true');

    const title = this.element('div', 'pip-confirm__title', overlay);
    title.textContent = t('END_CALL_TITLE');
    const message = this.element('div', 'pip-confirm__message', overlay);
    message.textContent = t('END_CALL_DESCRIPTION');

    const actions = this.element('div', 'pip-confirm__actions', overlay);
    const end = this.textButton('pip-confirm__action pip-confirm__end', t('END_CALL'), actions, () =>
      this.controls.confirmEndCall(),
    );
    this.textButton('pip-confirm__action pip-confirm__resume', t('RESUME'), actions, () =>
      this.controls.resumeCall(),
    );
    end.focus();
    return overlay;
  }

  private toggleSettings(): void {
    if (this.settingsPanel) {
      this.closeSettings();
    } else {
      this.openSettings();
    }
  }

  private openSettings(): void {
    if (this.settingsPanel) return;

    const panel = this.element('section', 'pip-settings');
    panel.setAttribute('aria-label', this.controls.translate('SETTINGS'));
    const mic = this.deviceSelect(panel, 'MICROPHONE', (id) => this.controls.selectMic(id));
    const camera = this.deviceSelect(panel, 'CAMERA', (id) => this.controls.selectCamera(id));
    this.doc.body.insertBefore(panel, this.toolbar);

    this.settingsPanel = panel;
    this.settingsPanelHeight = panel.offsetHeight;
    this.resizeBy(this.settingsPanelHeight);
    this.settingsButton?.classList.add('pip-button--active');
    this.settingsButton?.setAttribute('aria-expanded', 'true');

    this.controls
      .listDevices()
      .then((devices) => {
        if (this.settingsPanel !== panel) return;
        this.fillDevices(mic, devices.mics, devices.micId, 'MICROPHONE');
        if (devices.cameraError) {
          this.showDeviceError(camera, devices.cameraError);
        } else {
          this.fillDevices(camera, devices.cameras, devices.cameraId, 'CAMERA');
        }
      })
      .catch(() => undefined);
  }

  private closeSettings(): void {
    if (!this.settingsPanel) return;
    this.settingsPanel.remove();
    this.settingsPanel = null;
    this.resizeBy(-this.settingsPanelHeight);
    this.settingsButton?.classList.remove('pip-button--active');
    this.settingsButton?.setAttribute('aria-expanded', 'false');
  }

  private resizeBy(height: number): void {
    try {
      this.pipWindow.resizeBy(0, height);
    } catch {
      // Resizing is best-effort: without it the tiles shrink to make room for the panel.
    }
  }

  private deviceSelect(
    panel: HTMLElement,
    labelKey: string,
    onSelect: (deviceId: string) => void,
  ): HTMLSelectElement {
    const field = this.element('label', 'pip-settings__field');
    const label = this.element('span', 'pip-settings__label', field);
    label.textContent = this.controls.translate(labelKey, 'settings');

    const select = this.element('select', 'pip-settings__select');
    select.disabled = true;
    select.addEventListener('change', () => onSelect(select.value));
    field.appendChild(select);
    panel.appendChild(field);
    return select;
  }

  private fillDevices(
    select: HTMLSelectElement,
    devices: ScreenSharePipDevice[],
    selectedId: string,
    labelKey: string,
  ): void {
    const fallback = this.controls.translate(labelKey, 'settings');
    select.replaceChildren(
      ...devices.map(({ id, label }, index) => {
        const option = this.doc.createElement('option');
        option.value = id;
        option.textContent = label || `${fallback} ${index + 1}`;
        return option;
      }),
    );
    if (devices.some(({ id }) => id === selectedId)) select.value = selectedId;
    select.disabled = devices.length === 0;
  }

  private showDeviceError(select: HTMLSelectElement, message: string): void {
    const error = this.element('div', 'pip-settings__error');
    this.setIcon(error, this.icon('alert-circle'));
    error.append(message);
    select.replaceWith(error);
  }

  private element<K extends keyof HTMLElementTagNameMap>(
    tag: K,
    className: string,
    parent?: HTMLElement,
  ): HTMLElementTagNameMap[K] {
    const el = this.doc.createElement(tag);
    el.className = className;
    parent?.appendChild(el);
    return el;
  }

  private button(
    className: string,
    label: string,
    svg: string,
    parent: HTMLElement,
    onClick: () => void,
  ): HTMLButtonElement {
    const btn = this.element('button', className);
    btn.type = 'button';
    btn.title = label;
    btn.setAttribute('aria-label', label);
    this.setIcon(btn, svg);
    btn.addEventListener('click', () => {
      onClick();
      this.refresh();
    });
    parent.appendChild(btn);
    return btn;
  }

  private cameraAccessTooltip(anchor: HTMLElement): HTMLElement {
    const tooltip = this.element('div', 'pip-tooltip', this.doc.body);
    tooltip.id = 'pip-camera-tooltip';
    tooltip.setAttribute('role', 'tooltip');
    tooltip.textContent = this.controls.translate('BROWSER_ACCESS_DENIED');
    tooltip.hidden = true;

    const show = () => {
      if (!this.controls.isCameraUnavailable()) return;
      tooltip.hidden = false;
      const rect = anchor.getBoundingClientRect();
      const margin = 8;
      const maxLeft = this.pipWindow.innerWidth - tooltip.offsetWidth - margin;
      const left = rect.left + rect.width / 2 - tooltip.offsetWidth / 2;
      tooltip.style.left = `${Math.max(margin, Math.min(left, maxLeft))}px`;
      tooltip.style.top = `${rect.top - tooltip.offsetHeight - margin}px`;
    };
    const hide = () => (tooltip.hidden = true);
    anchor.addEventListener('mouseenter', show);
    anchor.addEventListener('focus', show);
    anchor.addEventListener('mouseleave', hide);
    anchor.addEventListener('blur', hide);
    return tooltip;
  }

  private placeIndicator(
    tile: HTMLElement,
    size: QaiCallAudioIndicatorSize,
    status: QaiCallAudioIndicatorStatus,
  ): HTMLElement {
    const slot = this.element('div', `pip-indicator pip-indicator--${status}`, tile);
    slot.setAttribute('aria-hidden', 'true');
    slot.hidden = true;
    slot.appendChild(this.indicator(size, status));
    return slot;
  }

  private textButton(
    className: string,
    label: string,
    parent: HTMLElement,
    onClick: () => void,
  ): HTMLButtonElement {
    const btn = this.element('button', className);
    btn.type = 'button';
    btn.textContent = label;
    btn.addEventListener('click', () => {
      onClick();
      this.refresh();
    });
    parent.appendChild(btn);
    return btn;
  }

  private setIcon(btn: HTMLElement, svg: string): void {
    if (this.currentIcons.get(btn) === svg) return;
    this.currentIcons.set(btn, svg);
    const parsed = new DOMParser().parseFromString(svg, 'image/svg+xml');
    btn.replaceChildren(parsed.documentElement);
  }
}
